import { Button } from '@app/components/various/Button';
import { Modal } from '@app/components/various/Modal';
import { Skeleton } from '@app/components/various/Skeleton';
import { SkeletonLayout } from '@app/components/various/SkeletonLayout';
import { addShippingAddress, getInitialSelectedShippingAddressId, updateTotalsByShippingAddress } from '@app/ducks';
import { getIsAccountEditingEnabled } from '@app/ducks/config';
import { getUser } from '@app/ducks/user';
import { getIsSeller } from '@app/logic/User';
import { useModalToggle } from '@app/utils/hooks/useModalToggle';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Addresses, Checkout, CheckoutForm } from '@typings';
import React from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { AddShippingAddressForm } from './AddShippingAddressForm';
import { SHIPPING_ADDRESSES_TO_SHOW, ShippingAddressRadioGroupField } from './ShippingAddressRadioGroupField';

interface Props {
  checkoutFields: Checkout.Requirements;
  shippingAddresses: Addresses.MixedShipping[] | undefined;
  isLoading: boolean;
}

export const CheckoutShippingAddress = ({ shippingAddresses = [], isLoading, checkoutFields }: Props) => {
  const { t } = useTranslation(['shipping', 'common']);

  const dispatch = useDispatch();
  const isAccountEditingEnabled = useSelector(getIsAccountEditingEnabled);
  const hasShippingAddressInCheckout = isDefined(checkoutFields.shippingAddress);
  const initialSelectedShippingAddressId = useSelector(getInitialSelectedShippingAddressId);

  const {
    fieldState: { error },
  } = useController({ name: 'shippingAddressId' });

  const errorMessage = error?.message as string | undefined;
  const user = useSelector(getUser);
  const { isOpen, showModal, hideModal } = useModalToggle();
  const { setValue } = useFormContext<CheckoutForm>();

  const hasShippingAddresses = !isEmpty(shippingAddresses);
  const canAddAddress = (isAccountEditingEnabled && getIsSeller(user)) || hasShippingAddressInCheckout;
  const allAddresses = React.useMemo(() => {
    const initialSelectedAddress = shippingAddresses.find(({ shippingAddress }) => shippingAddress === initialSelectedShippingAddressId);

    return [initialSelectedAddress, ...shippingAddresses.filter(address => address !== initialSelectedAddress)].filter(isDefined);
  }, [shippingAddresses, initialSelectedShippingAddressId]);

  const onAddressAddedToAccount = (newAddress: Addresses.Shipping) => {
    dispatch(addShippingAddress(newAddress));
    dispatch(updateTotalsByShippingAddress({ shippingAddressId: newAddress.shippingAddress }));
    setValue('shippingAddressId', newAddress.shippingAddress, { shouldValidate: true });
    hideModal();
  };

  const onAddressAdded = (newAddress: Addresses.TemporaryShipping) => {
    const { isTemporary, shippingAddress, ...temporaryShippingAddress } = newAddress;
    dispatch(updateTotalsByShippingAddress({ shippingAddress: temporaryShippingAddress }));
    dispatch(addShippingAddress(newAddress));
    setValue('shippingAddressId', newAddress.shippingAddress, { shouldDirty: true, shouldValidate: true });
    hideModal();
  };

  if (!isDefined(checkoutFields.shippingAddressId)) {
    return null;
  }

  return (
    <div className="box">
      <div className="mb-5 flex items-center justify-between">
        <h3 className="heading-3">{t('shipping:shipping_address_one')}</h3>
        {canAddAddress && (
          <Button variant="bordered" color="secondary" onClick={showModal}>
            {t('shipping:add_address')}
          </Button>
        )}
      </div>
      {isLoading ?
        <SkeletonLayout skeleton={<Skeleton type="rectangle" />} repeatLayout={SHIPPING_ADDRESSES_TO_SHOW} isLoading />
      : !hasShippingAddresses ?
        <div>
          <span className="body-2">{canAddAddress ? t('shipping:no_shipping_addresses') : t('shipping:no_shipping_addresses_buyer')}</span>
          {isDefined(errorMessage) && <div className="text-error-45 body-5">{errorMessage}</div>}
        </div>
      : <div>
          <ShippingAddressRadioGroupField name="shippingAddressId" options={allAddresses} />
        </div>
      }
      <Modal title={t('shipping:add')} isOpen={isOpen} onClose={hideModal}>
        <AddShippingAddressForm
          hasShippingAddressInCheckout={hasShippingAddressInCheckout}
          onCancel={hideModal}
          onAddressAdded={onAddressAdded}
          onAddressAddedToAccount={onAddressAddedToAccount}
        />
      </Modal>
    </div>
  );
};
